import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  FileText, Lightbulb, Loader2, MessageSquareText, Paperclip, Pencil, Plus,
} from "lucide-react";
import { useState } from "react";
import BaseDrawer from "@/shared/components/Drawer";
import { Badge } from "@/shared/ui/Badge";
import { Button } from "@/shared/ui/Button";
import {
  SUGGESTION_CATEGORY_LABELS,
  SUGGESTION_STATUSES,
  SUGGESTION_SUBCATEGORIES,
} from "../configs/constants";
import { useSuggestions } from "../hooks/useSuggestionMutations";
import RichAnswerDialog from "./RichAnswerDialog";
import SuggestionFormDialog from "./SuggestionFormDialog";
import type { SuggestionCategory, UserSuggestion } from "../types/suggestions.types";

type SuggestionsDrawerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
};

const getStatusInfo = (value: string) =>
  SUGGESTION_STATUSES.find((s) => s.value === value) ?? { label: value, variant: "secondary" as const };

const getStatusBadgeVariant = (status: string) => {
  switch (status) {
    case "pending": return "warning" as const;
    case "reviewed": return "default" as const;
    case "planned": return "default" as const;
    case "completed": return "success" as const;
    case "rejected": return "destructiveSoft" as const;
    default: return "secondary" as const;
  }
};

const getSubcategoryLabel = (category: SuggestionCategory, subcategory: string) => {
  const options = SUGGESTION_SUBCATEGORIES[category];
  return options?.find((o) => o.value === subcategory)?.label ?? subcategory;
};

const SuggestionCard = ({
  suggestion,
  onEdit,
  onOpenRichAnswer,
}: {
  suggestion: UserSuggestion
  onEdit: (suggestion: UserSuggestion) => void
  onOpenRichAnswer: (html: string) => void
}) => {
  const statusInfo = getStatusInfo(suggestion.status);
  const isPending = suggestion.status === "pending";
  const richAnswerHtml = suggestion.admin_notes_html;
  // Absent tant que l'API ne l'expose pas
  const answerAttachments = suggestion.admin_notes_attachments ?? [];

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2">
      {/* Header: date + status + edit */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[11px] text-slate-500">
            {format(new Date(suggestion.created_at), "dd MMM yyyy à HH:mm", { locale: fr })}
          </span>
          <Badge variant={getStatusBadgeVariant(suggestion.status)}>
            {statusInfo.label}
          </Badge>
        </div>
        {isPending && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs shrink-0"
            onClick={() => onEdit(suggestion)}
          >
            <Pencil className="h-3 w-3 mr-1" />
            Modifier
          </Button>
        )}
      </div>

      {/* Category + subcategory */}
      <div className="flex items-center gap-1.5 text-[11px]">
        <span className="text-slate-500">{SUGGESTION_CATEGORY_LABELS[suggestion.category]}</span>
        <span className="text-slate-300">·</span>
        <span className="text-slate-600">{getSubcategoryLabel(suggestion.category, suggestion.subcategory)}</span>
      </div>

      {/* Message */}
      <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">
        {suggestion.message}
      </p>

      {/* Attachment */}
      {suggestion.attachment && (
        <a
          href={suggestion.attachment.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800"
        >
          <Paperclip className="h-3 w-3" />
          {suggestion.attachment.filename}
        </a>
      )}

      {/* Admin notes */}
      {(suggestion.admin_notes || suggestion.admin_notes_html || answerAttachments.length > 0) && (
        <div className="rounded bg-slate-50 border border-slate-200 px-2.5 py-2 mt-1 space-y-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">
            Réponse de l'équipe
          </div>
          {suggestion.admin_notes && (
            <p className="text-xs text-slate-700 whitespace-pre-line">{suggestion.admin_notes}</p>
          )}
          {richAnswerHtml && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => onOpenRichAnswer(richAnswerHtml)}
            >
              <FileText className="h-3 w-3 mr-1" />
              Voir la réponse détaillée
            </Button>
          )}
          {answerAttachments.length > 0 && (
            <ul className="flex flex-wrap gap-1.5">
              {answerAttachments.map((file) => (
                <li key={file.signed_id}>
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={file.filename}
                    className="flex h-16 w-24 items-center justify-center overflow-hidden rounded border border-slate-200 bg-white hover:opacity-90"
                  >
                    {file.content_type.startsWith("image/")
                      ? <img src={file.url} alt={file.filename} loading="lazy" className="h-full w-full object-cover" />
                      : (
                        <span className="flex flex-col items-center gap-1 px-1.5 text-[10px] text-slate-500">
                          <FileText className="h-4 w-4" />
                          <span className="max-w-full truncate">{file.filename}</span>
                        </span>
                      )}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Page path */}
      {suggestion.page_path && (
        <div className="text-[10px] text-slate-400 truncate">
          Page :
          {" "}
          {suggestion.page_path}
        </div>
      )}
    </div>
  );
};

const SuggestionsDrawer = ({ open, onOpenChange }: SuggestionsDrawerProps) => {
  const [formOpen, setFormOpen] = useState(false);
  const [editingSuggestion, setEditingSuggestion] = useState<UserSuggestion | null>(null);
  const [richAnswerHtml, setRichAnswerHtml] = useState<string | null>(null);

  const { data, isLoading } = useSuggestions({ page: 1, limit: 50 }, open);
  const suggestions = data?.user_suggestions ?? [];

  const handleCreate = () => {
    setEditingSuggestion(null);
    setFormOpen(true);
  };

  const handleEdit = (suggestion: UserSuggestion) => {
    setEditingSuggestion(suggestion);
    setFormOpen(true);
  };

  return (
    <>
      <BaseDrawer
        open={open}
        onOpenChange={onOpenChange}
        title={(
          <span className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5" />
            Mes suggestions
          </span>
        )}
        description="Suivez vos suggestions et leur avancement."
        width="480px"
      >
        <div className="p-3 space-y-3">
          {/* Create button */}
          <Button
            type="button"
            variant="action"
            size="sm"
            className="w-full h-8 text-xs"
            onClick={handleCreate}
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Nouvelle suggestion
          </Button>

          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-8 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Chargement...
            </div>
          )}

          {/* Empty state */}
          {!isLoading && suggestions.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <MessageSquareText className="h-10 w-10 text-slate-300 mb-3" />
              <p className="text-sm text-slate-500">Aucune suggestion pour le moment.</p>
              <p className="text-xs text-slate-400 mt-1">Cliquez sur le bouton ci-dessus pour en créer une.</p>
            </div>
          )}

          {/* List */}
          {!isLoading && suggestions.map((suggestion) => (
            <SuggestionCard
              key={suggestion.id}
              suggestion={suggestion}
              onEdit={handleEdit}
              onOpenRichAnswer={setRichAnswerHtml}
            />
          ))}
        </div>
      </BaseDrawer>

      <SuggestionFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        suggestion={editingSuggestion}
      />

      <RichAnswerDialog
        html={richAnswerHtml}
        open={!!richAnswerHtml}
        onOpenChange={() => setRichAnswerHtml(null)}
      />
    </>
  );
};

export default SuggestionsDrawer;
